import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Slide,
  styled,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import AdminApi from "../../components/AdminApi/Api/api";
import CenterRegistrationApi from "../../components/centerRegistration/Api/api";
import {
  canViewTicketAcceptance,
  canManageTicketAcceptance,
} from "../../utility/permissionUtils";

// Modern color palette
const COLORS = {
  primary: {
    main: "#6366F1",
    light: "#818CF8",
    dark: "#4F46E5",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#10B981",
    light: "#34D399",
    dark: "#059669",
    contrastText: "#FFFFFF",
  },
  accent: {
    main: "#F59E0B",
    light: "#FBBF24",
    dark: "#D97706",
    contrastText: "#FFFFFF",
  },
  background: {
    light: "#F8FAFC",
    paper: "#FFFFFF",
    dark: "#F1F5F9",
  },
  text: {
    primary: "#1E293B",
    secondary: "#64748B",
    disabled: "#94A3B8",
  },
  status: {
    success: "#10B981",
    error: "#EF4444",
    warning: "#F59E0B",
    info: "#3B82F6",
  },
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "85%", md: "600px" },
  bgcolor: "background.paper",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
  borderRadius: "16px",
  p: { xs: 3, sm: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
  outline: "none",
  border: `1px solid ${alpha(COLORS.primary.main, 0.1)}`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: `0 4px 20px ${alpha(COLORS.primary.main, 0.08)}`,
  background: `linear-gradient(145deg, ${COLORS.background.paper} 0%, ${alpha(
    COLORS.background.light,
    0.8
  )} 100%)`,
  transition: "all 0.3s ease",
  border: `1px solid ${alpha(COLORS.primary.main, 0.05)}`,
  "&:hover": {
    boxShadow: `0 8px 30px ${alpha(COLORS.primary.main, 0.12)}`,
    transform: "translateY(-4px)",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "12px",
  padding: "10px 20px",
  boxShadow: `0 4px 6px ${alpha(COLORS.primary.main, 0.1)}`,
  transition: "all 0.2s ease",
  background: `linear-gradient(45deg, ${COLORS.primary.main} 0%, ${COLORS.primary.light} 100%)`,
  color: COLORS.primary.contrastText,
  "&:hover": {
    boxShadow: `0 6px 12px ${alpha(COLORS.primary.main, 0.2)}`,
    transform: "translateY(-2px)",
    background: `linear-gradient(45deg, ${COLORS.primary.dark} 0%, ${COLORS.primary.main} 100%)`,
  },
  "&.Mui-disabled": {
    background: COLORS.background.dark,
    color: COLORS.text.disabled,
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "12px",
  padding: "10px 20px",
  border: `1px solid ${alpha(COLORS.text.secondary, 0.2)}`,
  color: COLORS.text.secondary,
  "&:hover": {
    border: `1px solid ${alpha(COLORS.primary.main, 0.5)}`,
    color: COLORS.primary.main,
    backgroundColor: alpha(COLORS.primary.main, 0.04),
  },
}));

const StatusChip = styled(Chip)(({ status, theme }) => {
  let color;
  switch (status) {
    case "enabled":
      color = COLORS.status.success;
      break;
    case "disabled":
      color = COLORS.status.error;
      break;
    case "warning":
      color = COLORS.status.warning;
      break;
    default:
      color = COLORS.text.secondary;
  }

  return {
    backgroundColor: alpha(color, 0.1),
    color: color,
    fontWeight: 600,
    borderRadius: "6px",
    border: `1px solid ${alpha(color, 0.2)}`,
  };
});

const CustomSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: COLORS.primary.main,
    "&:hover": {
      backgroundColor: alpha(COLORS.primary.main, 0.08),
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: COLORS.primary.main,
  },
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  color: COLORS.text.primary,
  fontWeight: 600,
  marginBottom: "16px",
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: "-6px",
    left: 0,
    width: "40px",
    height: "3px",
    backgroundColor: COLORS.primary.main,
    borderRadius: "3px",
  },
}));

export default function TicketAcceptanceManager({ userPermissions }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [primaryServices, setPrimaryServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [ticketAcceptances, setTicketAcceptances] = useState([]);
  const [selectedPrimaryService, setSelectedPrimaryService] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [ticketSettings, setTicketSettings] = useState({
    is_critical: true,
    is_authorized: false,
    is_subscription: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch primary services
  const fetchPrimaryServices = async () => {
    try {
      const response = await new CenterRegistrationApi().getAllServices();
      setPrimaryServices(response.data);
    } catch (error) {
      console.error("Error fetching primary services:", error);
      showSnackbar("Failed to fetch primary services", "error");
    }
  };

  // Fetch clients
  const fetchClients = async () => {
    try {
      const response = await new AdminApi().getAllClients();
      // Handle different response formats
      const clientsData = response.data || response;
      setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      showSnackbar("Failed to fetch clients", "error");
    }
  };

  // Fetch ticket acceptances
  const fetchTicketAcceptances = async () => {
    if (!selectedPrimaryService || !selectedClient) return;

    setLoading(true);
    try {
      const response = await new AdminApi().getAcceptanceServiceTickets(
        selectedPrimaryService,
        selectedClient
      );

      // Handle the actual response format
      if (
        response.data &&
        response.data.result &&
        response.data.result.length > 0
      ) {
        const clientData = response.data.result[0];
        setTicketAcceptances(clientData.ticketAcceptance || []);
      } else {
        setTicketAcceptances([]);
      }
    } catch (error) {
      console.error("Error fetching ticket acceptances:", error);
      showSnackbar("Failed to fetch ticket acceptances", "error");
      setTicketAcceptances([]);
    } finally {
      setLoading(false);
    }
  };

  // Save ticket acceptance settings
  const saveTicketAcceptance = async () => {
    if (!canManageTicketAcceptance) {
      showSnackbar(
        "You don't have permission to manage ticket acceptance settings",
        "error"
      );
      return;
    }

    if (!selectedPrimaryService || !selectedClient) {
      showSnackbar("Please select both primary service and client", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client_id: selectedClient,
        primary_service_id: selectedPrimaryService,
        ticketAcceptance: ticketSettings,
      };

      const response = await new AdminApi().updateServiceTicketAcceptance(
        payload
      );

      if (response.success) {
        showSnackbar("Ticket acceptance settings saved successfully");
        setModalOpen(false);
        resetForm();
        fetchTicketAcceptances();
      } else {
        showSnackbar(response.message || "Failed to save settings", "error");
      }
    } catch (error) {
      console.error("Error saving ticket acceptance:", error);
      showSnackbar(
        error.response?.data?.message ||
          "Failed to save ticket acceptance settings",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (canViewTicketAcceptance) {
      fetchPrimaryServices();
      fetchClients();
    }
  }, []);

  useEffect(() => {
    if (selectedPrimaryService && selectedClient && canViewTicketAcceptance) {
      fetchTicketAcceptances();
    }
  }, [selectedPrimaryService, selectedClient]);

  const handleSwitchChange = (field) => (event) => {
    setTicketSettings({
      ...ticketSettings,
      [field]: event.target.checked,
    });
  };

  const resetForm = () => {
    setSelectedPrimaryService("");
    setSelectedClient("");
    setTicketSettings({
      is_critical: true,
      is_authorized: false,
      is_subscription: false,
    });
  };

  // Get client name for display
  const getClientName = (clientId) => {
    const client = clients.find((c) => c._id === clientId);
    return client
      ? client.name || client.email || client.client_name || "Unknown Client"
      : "Unknown Client";
  };

  // Get primary service name for display
  const getPrimaryServiceName = (serviceId) => {
    const service = primaryServices.find((s) => s._id === serviceId);
    return service ? service.service_name : "Unknown Service";
  };

  // Don't render if user doesn't have permission
  if (!canViewTicketAcceptance) {
    return null;
  }

  return (
    <>
      <Box sx={{ background: COLORS.background.light }}>
        <StyledCard>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SettingsIcon sx={{ mr: 1.5, color: COLORS.primary.main }} />
                <Typography
                  variant="h5"
                  fontWeight="700"
                  color={COLORS.text.primary}
                >
                  Ticket Acceptance Management
                </Typography>
              </Box>
            }
            action={
              canManageTicketAcceptance && (
                <ActionButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setModalOpen(true)}
                >
                  New Configuration
                </ActionButton>
              )
            }
            sx={{ pb: 2 }}
          />
          <Divider sx={{ borderColor: alpha(COLORS.primary.main, 0.1) }} />
          <CardContent sx={{ pt: 3 }}>
            <SectionHeader variant="h6">Filter Settings</SectionHeader>

            <Grid container spacing={2} className="mb-4">
              <Grid item xs={12} md={6} className="mt-4" sx={{ minWidth: 200 }}>
                <FormControl fullWidth>
                  <InputLabel>Primary Service</InputLabel>
                  <Select
                    value={selectedPrimaryService}
                    onChange={(e) => setSelectedPrimaryService(e.target.value)}
                    label="Primary Service"
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha(COLORS.primary.main, 0.2),
                      },
                    }}
                  >
                    {primaryServices.map((service) => (
                      <MenuItem key={service._id} value={service._id}>
                        {service.service_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} className="mt-4" sx={{ minWidth: 200 }}>
                <FormControl fullWidth>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    label="Client"
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha(COLORS.primary.main, 0.2),
                      },
                    }}
                  >
                    {clients.map((client) => (
                      <MenuItem key={client._id} value={client._id}>
                        {client.name ||
                          client.email ||
                          client.client_name ||
                          "Unknown Client"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {loading ? (
              <Box className="flex justify-center p-6">
                <CircularProgress sx={{ color: COLORS.primary.main }} />
              </Box>
            ) : ticketAcceptances.length > 0 ? (
              <Box>
                <SectionHeader variant="h6" sx={{ mt: 3 }}>
                  Current Configurations
                </SectionHeader>

                <Grid container spacing={2}>
                  {ticketAcceptances.map((acceptance, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: "12px",
                          border: `1px solid ${alpha(
                            COLORS.primary.main,
                            0.1
                          )}`,
                          background: `linear-gradient(145deg, ${
                            COLORS.background.paper
                          } 0%, ${alpha(COLORS.background.light, 0.5)} 100%)`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: `0 6px 16px ${alpha(
                              COLORS.primary.main,
                              0.1
                            )}`,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            color={COLORS.text.primary}
                            sx={{ mb: 2 }}
                          >
                            {getPrimaryServiceName(
                              acceptance.primaryServiceId?._id ||
                                acceptance.primaryServiceId
                            )}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color={COLORS.text.secondary}
                              >
                                Critical Tickets
                              </Typography>
                              <StatusChip
                                label={
                                  acceptance.is_critical
                                    ? "Enabled"
                                    : "Disabled"
                                }
                                status={
                                  acceptance.is_critical
                                    ? "enabled"
                                    : "disabled"
                                }
                                size="small"
                              />
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color={COLORS.text.secondary}
                              >
                                Authorization Required
                              </Typography>
                              <StatusChip
                                label={acceptance.is_authorized ? "Yes" : "No"}
                                status={
                                  acceptance.is_authorized
                                    ? "enabled"
                                    : "disabled"
                                }
                                size="small"
                              />
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color={COLORS.text.secondary}
                              >
                                Subscription Based
                              </Typography>
                              <StatusChip
                                label={
                                  acceptance.is_subscription ? "Yes" : "No"
                                }
                                status={
                                  acceptance.is_subscription
                                    ? "enabled"
                                    : "warning"
                                }
                                size="small"
                              />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : selectedPrimaryService && selectedClient ? (
              <Box
                className="text-center p-6 rounded-lg"
                sx={{
                  background: `linear-gradient(145deg, ${alpha(
                    COLORS.background.dark,
                    0.5
                  )} 0%, ${alpha(COLORS.background.light, 0.5)} 100%)`,
                  border: `1px dashed ${alpha(COLORS.primary.main, 0.2)}`,
                  borderRadius: "12px",
                }}
              >
                <Typography variant="body2" color={COLORS.text.secondary}>
                  No ticket acceptance settings found for{" "}
                  <Box
                    component="span"
                    fontWeight="600"
                    color={COLORS.primary.main}
                  >
                    {getClientName(selectedClient)}
                  </Box>{" "}
                  and{" "}
                  <Box
                    component="span"
                    fontWeight="600"
                    color={COLORS.primary.main}
                  >
                    {getPrimaryServiceName(selectedPrimaryService)}
                  </Box>
                  .
                </Typography>
              </Box>
            ) : (
              <Box
                className="text-center p-6 rounded-lg"
                sx={{
                  background: `linear-gradient(145deg, ${alpha(
                    COLORS.background.dark,
                    0.5
                  )} 0%, ${alpha(COLORS.background.light, 0.5)} 100%)`,
                  border: `1px dashed ${alpha(COLORS.primary.main, 0.2)}`,
                  borderRadius: "12px",
                }}
              >
                <Typography variant="body2" color={COLORS.text.secondary}>
                  Please select a primary service and client to view ticket
                  acceptance settings.
                </Typography>
              </Box>
            )}
          </CardContent>
        </StyledCard>

        {/* Add/Edit Modal */}
        {canManageTicketAcceptance && (
          <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
            <Box sx={modalStyle}>
              <Box className="flex justify-between items-center mb-4">
                <Typography
                  variant="h6"
                  fontWeight="700"
                  color={COLORS.text.primary}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <SettingsIcon sx={{ mr: 1, color: COLORS.primary.main }} />
                    Ticket Acceptance Configuration
                  </Box>
                </Typography>
                <IconButton
                  onClick={() => setModalOpen(false)}
                  size="small"
                  sx={{
                    color: COLORS.text.secondary,
                    "&:hover": { color: COLORS.primary.main },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6} sx={{ minWidth: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel>Primary Service </InputLabel>
                    <Select
                      value={selectedPrimaryService}
                      onChange={(e) =>
                        setSelectedPrimaryService(e.target.value)
                      }
                      label="Primary Service *"
                      required
                      sx={{
                        borderRadius: "12px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: alpha(COLORS.primary.main, 0.2),
                        },
                      }}
                    >
                      {primaryServices.map((service) => (
                        <MenuItem key={service._id} value={service._id}>
                          {service.service_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6} sx={{ minWidth: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={selectedClient}
                      onChange={(e) => setSelectedClient(e.target.value)}
                      label="Client *"
                      required
                      sx={{
                        borderRadius: "12px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: alpha(COLORS.primary.main, 0.2),
                        },
                      }}
                    >
                      {clients.map((client) => (
                        <MenuItem key={client._id} value={client._id}>
                          {client.name ||
                            client.email ||
                            client.client_name ||
                            "Unknown Client"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: "12px",
                      border: `1px solid ${alpha(COLORS.primary.main, 0.1)}`,
                      background: COLORS.background.light,
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        fontWeight="600"
                        color={COLORS.text.primary}
                        sx={{ mb: 3 }}
                      >
                        Ticket Settings
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <CustomSwitch
                              checked={ticketSettings.is_critical}
                              disabled // Disable the switch
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="body1"
                                fontWeight="500"
                                color={COLORS.text.primary}
                              >
                                Critical Tickets
                              </Typography>
                              <Typography
                                variant="caption"
                                color={COLORS.text.secondary}
                              >
                                Critical tickets are always enabled by default
                              </Typography>
                            </Box>
                          }
                          sx={{ m: 0 }}
                        />

                        <FormControlLabel
                          control={
                            <CustomSwitch
                              checked={ticketSettings.is_authorized}
                              onChange={handleSwitchChange("is_authorized")}
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="body1"
                                fontWeight="500"
                                color={COLORS.text.primary}
                              >
                                Authorization Required
                              </Typography>
                              <Typography
                                variant="caption"
                                color={COLORS.text.secondary}
                              >
                                Require manager approval for these tickets
                              </Typography>
                            </Box>
                          }
                          sx={{ m: 0 }}
                        />

                        <FormControlLabel
                          control={
                            <CustomSwitch
                              checked={ticketSettings.is_subscription}
                              onChange={handleSwitchChange("is_subscription")}
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="body1"
                                fontWeight="500"
                                color={COLORS.text.primary}
                              >
                                Subscription Based
                              </Typography>
                              <Typography
                                variant="caption"
                                color={COLORS.text.secondary}
                              >
                                Link ticket creation to active subscriptions
                              </Typography>
                            </Box>
                          }
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box className="flex justify-end gap-3 mt-6">
                <SecondaryButton onClick={() => setModalOpen(false)}>
                  Cancel
                </SecondaryButton>
                <ActionButton
                  onClick={saveTicketAcceptance}
                  disabled={
                    saving || !selectedPrimaryService || !selectedClient
                  }
                  startIcon={
                    saving ? <CircularProgress size={16} /> : <CheckIcon />
                  }
                >
                  {saving ? "Saving..." : "Save Configuration"}
                </ActionButton>
              </Box>
            </Box>
          </Modal>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={Slide}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
              alignItems: "center",
              fontWeight: 500,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
